import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Fingerprint, Loader2, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { verifyBiometric } from '../portal/biometric';
import PasskeyList from '../portal/PasskeyList';
import PasskeyPrompt from '../portal/PasskeyPrompt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const msg = (e) => e?.response?.data?.detail || (e?.name === 'NotAllowedError' ? 'Verifica annullata: riprova o usa il codice email.' : 'Verifica non riuscita');

export const adminClient = (token) => {
  const h = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  return { get: (p) => axios.get(`${API}${p}`, h), post: (p, b) => axios.post(`${API}${p}`, b, h), delete: (p) => axios.delete(`${API}${p}`, h) };
};

export function AdminMfa({ challenge, onToken, onBack }) {
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const bio = async () => {
    setBusy(true); setErr('');
    try { onToken((await verifyBiometric(adminClient(), '/admin/passkeys', challenge.mfa_token)).token); } catch (e) { setErr(msg(e)); } finally { setBusy(false); }
  };
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (challenge.passkey) bio(); else send();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => { try { await axios.post(`${API}/admin/mfa/email`, { mfa_token: challenge.mfa_token }); setSent(true); } catch (e) { setErr(msg(e)); } };
  const verify = async () => { setBusy(true); try { onToken((await axios.post(`${API}/admin/mfa/verify`, { mfa_token: challenge.mfa_token, code })).data.token); } catch (e) { setErr(msg(e)); } finally { setBusy(false); } };
  return (
    <div className="bg-[#0A0A0A] border border-[#1f2937] rounded-sm p-8 space-y-4" data-testid="admin-mfa">
      <p className="text-sm text-[#E5E5E5]">{challenge.passkey ? 'Conferma l\'accesso con impronta, Face ID o Windows Hello.' : 'Ti abbiamo inviato un codice via email: inseriscilo per entrare.'}</p>
      {err && <p className="text-sm text-red-400" data-testid="admin-mfa-error">{err}</p>}
      {challenge.passkey && <Button onClick={bio} disabled={busy} className="bond-btn bond-btn-primary w-full" data-testid="admin-mfa-passkey">{busy ? <Loader2 className="animate-spin" size={18} /> : <><Fingerprint size={18} className="mr-2" />Conferma con impronta o volto</>}</Button>}
      {challenge.email_fallback && (!sent ? <button onClick={send} className="text-xs text-[#D4AF37] inline-flex items-center gap-1 hover:underline" data-testid="admin-mfa-email"><Mail size={13} />Usa invece un codice via email</button> : (
        <div className="flex gap-2"><Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} placeholder="Codice email" className="input-smart rounded-none h-11 font-mono" data-testid="admin-mfa-code" />
          <Button onClick={verify} disabled={busy || code.length !== 6} className="bond-btn bond-btn-primary h-11" data-testid="admin-mfa-verify">Verifica</Button></div>))}
      <button onClick={onBack} className="text-xs text-[#A3A3A3] hover:text-white block" data-testid="admin-mfa-back">Torna al login</button>
    </div>
  );
}

export function AdminBiometricCard({ token }) {
  const c = adminClient(token);
  return (
    <div className="bg-[#0A0A0A] border border-[#1f2937] rounded-sm p-6 mb-6 text-[#E5E5E5]" data-testid="admin-biometric-card">
      <p className="font-semibold mb-4">Accesso biometrico amministratore</p>
      <PasskeyList client={c} base="/admin/passkeys" who="admin" />
    </div>
  );
}

export function AdminPasskeyPrompt({ token }) {
  return <PasskeyPrompt who="admin" client={adminClient(token)} base="/admin/passkeys" />;
}
